import { useState, useEffect, useMemo } from "react";
import { usePageLoadMetric } from "../hooks/usePageLoadMetric";
import { useLocation } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/atoms";
import { ResetIcon, RibbonCheckIcon as CheckIcon, CloseIcon as XIcon } from "../components/atoms/Icon";
import { TabBar } from "../components/molecules/TabBar";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import { updateTenantValue, deleteTenantValue, fetchTenantOverrides } from "../shared/dictionary/api";
import { MonthlyShiftCellSettings } from "../components/molecules/MonthlyShiftCellSettings";
import {
    type MonthlyShiftCellDisplaySettings,
    DEFAULT_MONTHLY_SHIFT_CELL_DISPLAY,
    migrateMonthlySettings,
} from "../shared/monthlyShiftSettings";
import { DailyShiftCellSettings } from "../components/molecules/DailyShiftCellSettings";
import {
    type DailyShiftCellDisplaySettings,
    DEFAULT_DAILY_SHIFT_CELL_DISPLAY,
} from "../shared/dailyShiftSettings";
import { fetchDisplaySetting, saveDisplaySetting, SETTING_KEYS } from "../api/displaySettings";

import { extractEditableTerms, type EditableTerm, COMMON } from "@shift-management/shared/locales";

import i18n from "../i18n";
import { usePermission } from "../hooks/usePermission";

// タブのタイプ
type DisplaySettingTab = "dictionary" | "monthly-shift" | "daily-shift";

const TAB_PATH_MAP: Record<string, DisplaySettingTab> = {
    "/display-settings/dictionary": "dictionary",
    "/display-settings/monthly-shift": "monthly-shift",
    "/display-settings/daily-shift": "daily-shift",
};

const DisplaySettingsPage = () => {
    const menuTitle = useMenuTitle();
    const location = useLocation();
    const activeTab: DisplaySettingTab = TAB_PATH_MAP[location.pathname] ?? "dictionary";

    const { canEdit } = usePermission();

    // マスター定義から edit_flg: true の項目を取得
    const lang = (i18n.language ?? "ja") as "ja" | "en";
    const [remoteOverrides, setRemoteOverrides] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);

    usePageLoadMetric("display-settings", !isLoading);

    const editableTerms: EditableTerm[] = useMemo(
        () => extractEditableTerms({ common: COMMON }, lang, remoteOverrides),
        [lang, remoteOverrides],
    );

    // 文言設定用の状態
    const [localOverrides, setLocalOverrides] = useState<Record<string, string>>({});
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    // 月間シフト設定用の状態
    const [monthlyShiftSettings, setMonthlyShiftSettings] = useState<MonthlyShiftCellDisplaySettings>(
        DEFAULT_MONTHLY_SHIFT_CELL_DISPLAY
    );
    const [initialMonthlyShiftSettings, setInitialMonthlyShiftSettings] = useState<MonthlyShiftCellDisplaySettings>(
        DEFAULT_MONTHLY_SHIFT_CELL_DISPLAY
    );

    // ワースケ設定用の状態
    const [dailyShiftSettings, setDailyShiftSettings] = useState<DailyShiftCellDisplaySettings>(
        DEFAULT_DAILY_SHIFT_CELL_DISPLAY
    );
    const [initialDailyShiftSettings, setInitialDailyShiftSettings] = useState<DailyShiftCellDisplaySettings>(
        DEFAULT_DAILY_SHIFT_CELL_DISPLAY
    );

    // 会社ID（実際は認証コンテキストから取得）
    const companyId = 1;

    // すべての設定を一括読み込み
    useEffect(() => {
        const loadAll = async () => {
            setIsLoading(true);
            try {
                // 全APIを並列で取得
                const [monthlyJson, dailyJson, overridesData] = await Promise.all([
                    fetchDisplaySetting(companyId, SETTING_KEYS.MONTHLY_SHIFT_CELL),
                    fetchDisplaySetting(companyId, SETTING_KEYS.DAILY_SHIFT_CELL),
                    fetchTenantOverrides(),
                ]);

                // 月間シフト設定
                if (monthlyJson) {
                    const parsed = migrateMonthlySettings(JSON.parse(monthlyJson) as Record<string, unknown>);
                    setMonthlyShiftSettings(parsed);
                    setInitialMonthlyShiftSettings(parsed);
                }
                // ワースケ設定
                if (dailyJson) {
                    const parsed = JSON.parse(dailyJson) as DailyShiftCellDisplaySettings;
                    setDailyShiftSettings(parsed);
                    setInitialDailyShiftSettings(parsed);
                }
                // 文言上書き
                setRemoteOverrides(overridesData);
                const local: Record<string, string> = {};
                for (const [key, value] of Object.entries(overridesData)) {
                    local[key] = value;
                }
                setLocalOverrides(local);
            } catch (error) {
                console.error("Failed to load display settings:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadAll();
    }, [companyId]);

    // 文言設定のハンドラー
    const handleStartEdit = (fullKey: string) => {
        if (!canEdit) return;
        const term = editableTerms.find(t => t.fullKey === fullKey);
        const currentValue = localOverrides[fullKey] ?? term?.defaultValue ?? "";
        setEditingKey(fullKey);
        setEditingValue(currentValue);
    };

    const handleSaveEdit = () => {
        if (editingKey) {
            const term = editableTerms.find(t => t.fullKey === editingKey);
            const defaultValue = term?.defaultValue ?? "";

            if (editingValue !== defaultValue) {
                setLocalOverrides((prev) => ({
                    ...prev,
                    [editingKey]: editingValue,
                }));
            } else {
                // デフォルト値と同じ場合は上書きを削除
                setLocalOverrides((prev) => {
                    const newState = { ...prev };
                    delete newState[editingKey];
                    return newState;
                });
            }
            setEditingKey(null);
            setEditingValue("");
        }
    };

    const handleCancelEdit = () => {
        setEditingKey(null);
        setEditingValue("");
    };

    const handleResetValue = (fullKey: string) => {
        setLocalOverrides((prev) => {
            const newState = { ...prev };
            delete newState[fullKey];
            return newState;
        });
    };

    // 保存処理
    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage(null);

        try {
            if (activeTab === "dictionary") {
                // 文言設定の保存
                const savePromises = editableTerms.map(async (term) => {
                    const localValue = localOverrides[term.fullKey];
                    const remoteValue = remoteOverrides[term.fullKey];

                    if (localValue !== undefined && localValue !== term.defaultValue) {
                        // 上書き値がある場合 → 保存
                        if (localValue !== remoteValue) {
                            await updateTenantValue(term.fullKey, localValue);
                        }
                    } else if (remoteValue !== undefined) {
                        // ローカルで消されたがリモートにある → 削除
                        await deleteTenantValue(term.fullKey);
                    }
                });

                await Promise.all(savePromises);

                // Redis overrides を再取得して i18next に反映
                const freshOverrides = await fetchTenantOverrides();
                setRemoteOverrides(freshOverrides);
                // i18next にも反映（namespace ごとに addResourceBundle）
                for (const term of editableTerms) {
                    const override = freshOverrides[term.fullKey];
                    const value = override ?? term.defaultValue;
                    i18n.addResourceBundle(lang, term.ns, { [term.key]: value }, true, true);
                }
            } else if (activeTab === "monthly-shift") {
                // 月間シフト設定の保存
                await saveDisplaySetting(
                    companyId,
                    SETTING_KEYS.MONTHLY_SHIFT_CELL,
                    JSON.stringify(monthlyShiftSettings),
                );
                setInitialMonthlyShiftSettings(monthlyShiftSettings);
            } else if (activeTab === "daily-shift") {
                // ワースケ設定の保存
                await saveDisplaySetting(
                    companyId,
                    SETTING_KEYS.DAILY_SHIFT_CELL,
                    JSON.stringify(dailyShiftSettings),
                );
                setInitialDailyShiftSettings(dailyShiftSettings);
            }

            setSaveMessage({ type: "success", text: "保存しました" });

            // 3秒後にメッセージを消す
            setTimeout(() => setSaveMessage(null), 3000);
        } catch (error) {
            console.error("Failed to save settings:", error);
            setSaveMessage({ type: "error", text: "保存に失敗しました。もう一度お試しください。" });
        } finally {
            setIsSaving(false);
        }
    };

    // 変更があるかどうか
    const hasChanges = editableTerms.some(term => {
        const localValue = localOverrides[term.fullKey];
        const remoteValue = remoteOverrides[term.fullKey];
        // ローカルに上書きがある場合: リモートと異なれば変更あり
        if (localValue !== undefined) {
            return localValue !== (remoteValue ?? term.defaultValue);
        }
        // ローカルにないがリモートにある場合: リセットされた
        if (remoteValue !== undefined) {
            return true;
        }
        return false;
    });

    // 月間シフト設定の変更有無チェック
    const hasMonthlyShiftChanges =
        JSON.stringify(monthlyShiftSettings) !== JSON.stringify(initialMonthlyShiftSettings);

    // ワースケ設定の変更有無チェック
    const hasDailyShiftChanges =
        JSON.stringify(dailyShiftSettings) !== JSON.stringify(initialDailyShiftSettings);

    // タブ定義
    // タブコンテンツを描画
    const renderTabContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">読み込み中...</div>
                </div>
            );
        }

        switch (activeTab) {
            case "dictionary":
                return (
                    <div className="space-y-6">
                        {/* 説明 */}
                        <div className="text-sm text-gray-600">
                            システムで使用される文言をカスタマイズできます。変更後は「保存」ボタンを押してください。
                        </div>

                        {/* 文言一覧テーブル */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* ヘッダー */}
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                                <div className="col-span-3">項目</div>
                                <div className="col-span-3">デフォルト</div>
                                <div className="col-span-5">カスタム設定</div>
                                <div className="col-span-1"></div>
                            </div>

                            {/* 行 */}
                            <div className="divide-y divide-gray-100">
                                {editableTerms.map((term) => {
                                    const localValue = localOverrides[term.fullKey];
                                    const isModified = localValue !== undefined && localValue !== term.defaultValue;
                                    const currentValue = localValue ?? term.defaultValue;
                                    const isEditing = editingKey === term.fullKey;

                                    return (
                                        <div
                                            key={term.fullKey}
                                            className={`px-4 py-4 grid grid-cols-12 gap-4 items-center text-sm ${
                                                isModified ? "bg-yellow-50" : "hover:bg-gray-50"
                                            }`}
                                        >
                                            {/* 項目名 */}
                                            <div className="col-span-3">
                                                <div className="font-medium text-gray-800">{term.defaultValue}</div>
                                                <div className="text-xs text-gray-400 mt-0.5">{term.fullKey}</div>
                                            </div>

                                            {/* デフォルト値 */}
                                            <div className="col-span-3 text-gray-500">
                                                {term.defaultValue}
                                            </div>

                                            {/* カスタム設定 */}
                                            <div className="col-span-5">
                                                {isEditing ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            value={editingValue}
                                                            onChange={(e) => setEditingValue(e.target.value)}
                                                            className="flex-1 px-3 py-2 border border-brand-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                                                            autoFocus
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") handleSaveEdit();
                                                                if (e.key === "Escape") handleCancelEdit();
                                                            }}
                                                        />
                                                        <button
                                                            onClick={handleSaveEdit}
                                                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                                            title="確定"
                                                        >
                                                            <CheckIcon className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                                            title="キャンセル"
                                                        >
                                                            <XIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div
                                                        onClick={() => handleStartEdit(term.fullKey)}
                                                        className={`px-3 py-2 rounded-lg cursor-pointer border border-transparent hover:border-gray-300 hover:bg-white ${
                                                            isModified ? "text-brand-700 font-medium bg-brand-50" : "text-gray-700"
                                                        }`}
                                                    >
                                                        {currentValue}
                                                        {isModified && (
                                                            <span className="ml-2 text-xs text-brand-500">変更済み</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* アクション */}
                                            <div className="col-span-1 flex justify-end">
                                                {isModified && !isEditing && (
                                                    <button
                                                        onClick={() => handleResetValue(term.fullKey)}
                                                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
                                                        title="デフォルトに戻す"
                                                    >
                                                        <ResetIcon className="w-3 h-3" />
                                                        戻す
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {editableTerms.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                文言設定項目がありません
                            </div>
                        )}
                    </div>
                );

            case "monthly-shift":
                return (
                    <div className="space-y-6">
                        <MonthlyShiftCellSettings
                            value={monthlyShiftSettings}
                            onChange={setMonthlyShiftSettings}
                            readOnly={!canEdit}
                        />
                    </div>
                );

            case "daily-shift":
                return (
                    <div className="space-y-6">
                        <DailyShiftCellSettings
                            value={dailyShiftSettings}
                            onChange={setDailyShiftSettings}
                            readOnly={!canEdit}
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
            <div className="flex-1 flex flex-col overflow-hidden">
                <PageHeader title={menuTitle} />

                <TabBar />

                {/* コンテンツ */}
                <div className="relative flex-1 overflow-auto p-6">
                    <div className={activeTab === "monthly-shift" ? "" : activeTab === "daily-shift" ? "max-w-4xl" : "max-w-3xl"}>
                        {renderTabContent()}
                    </div>
                    {activeTab === "daily-shift" && (
                        <div className="absolute inset-0 bg-gray-400/30 pointer-events-auto z-10" />
                    )}
                </div>

                {/* フッター */}
                <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
                    {/* メッセージ表示 */}
                    <div className="flex-1">
                        {saveMessage && (
                            <span
                                className={`text-sm ${
                                    saveMessage.type === "success" ? "text-green-600" : "text-red-600"
                                }`}
                            >
                                {saveMessage.text}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-3">
                        {canEdit && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleSave}
                            disabled={isSaving || !(activeTab === "dictionary" ? hasChanges : activeTab === "monthly-shift" ? hasMonthlyShiftChanges : hasDailyShiftChanges)}
                            className="px-6"
                        >
                            {isSaving ? "保存中..." : "保存"}
                        </Button>
                        )}
                    </div>
                </div>
            </div>
    );
};



export default DisplaySettingsPage;
